<template>
    <AutoComplete
        ref="searchInputRef"
        v-model="searchString"
        :suggestions="filteredSuggestions"
        option-group-label="name"
        option-group-children="items"
        optionLabel="ticker"
        @complete="onSearch"
        @item-select="handleSelection"
        placeholder="Search ticker or company name"
        input-class="w-full placeholder:text-sm placeholder:font-light"
        variant="filled"
        panel-class="shadow-xl shadow-black/20"
        scroll-height="80vh"
        pt:optiongroup:class="text-primary font-bold tracking-wide border-b mt-5 first:mt-0 mb-3"
        pt:overlay:class="w-full max-w-screen-sm"
      >
        <template #option="slotProps">
            <div class="flex justify-between w-full gap-2">
                <div class="max-w-64 truncate">{{ slotProps.option.full_name }}</div>
                <div class="font-semibold">{{ slotProps.option.ticker }}</div>
            </div>
        </template>
    </AutoComplete>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useTickersStore } from "@/stores/Tickers";
import { useSectorsStore } from "@/stores/Sectors";
import { useDebounceFn } from "@vueuse/core";

import { AutoCompleteOptionSelectEvent } from "primevue/autocomplete";
import { IClickHandler } from "@/types/handlers/click-handler";
import type { SearchableTicker } from "@/types/entities";
import { usePermissionsStore } from "@/stores/Permissions";
import { storeToRefs } from "pinia";
import { reportSearchEvent } from "@/composables/user-activity-monitor";

const { hasEtfAccess, hasSectorAccess, hasStockAccess } = storeToRefs( usePermissionsStore() );

type SearchResult = {
    full_name: string,
    ticker: string,
    isStock?: boolean,
    isEtf?: boolean,
    isSector?: boolean,
};

type SearchGroupResult = {
    name: string,
    items: SearchResult[];
};

export interface ISearchProps {
    supportsStocks?: boolean,
    stockClickHandler?: IClickHandler,
    supportsEtfs?: boolean,
    etfClickHandler?: IClickHandler,
    supportsSectors?: boolean,
    sectorClickHandler?: IClickHandler,
}

const props = withDefaults( defineProps< ISearchProps >(), {
    supportsStocks: true,
    supportsEtfs: true,
    supportsSectors: true
} );

const tickersStore = useTickersStore();
const sectorsStore = useSectorsStore();

const searchInputRef = ref();

const searchString = ref< string >( '' );
const filteredSuggestions = ref< SearchGroupResult[] >( [] );

const debouncedSearch = useDebounceFn( () => {
  // Clear out results container
  filteredSuggestions.value.splice( 0 );

  // Normalize the search string
  const originalSearchString: string = searchString.value.toLocaleLowerCase();

  if ( hasStockAccess.value && props.supportsStocks ) {
    const matchedStocks = [ ...(
        tickersStore.searchableStocks.filter( ( st: SearchableTicker ) =>
            st.searchTicker.includes( originalSearchString ) ||
            st.searchName.includes( originalSearchString ) )
    ) ];

    if ( matchedStocks.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'Stocks',
        items: [ ...matchedStocks.map( stock => ( {
          full_name: stock.name,
          ticker: stock.ticker,
          isStock: true
        } ) ) ]
      } );
    }
  }

  if ( hasEtfAccess.value && props.supportsEtfs ) {
    const matchedEtfs = [ ...(
        tickersStore.searchableEtfs.filter( ( st: SearchableTicker ) =>
            st.searchTicker.includes( originalSearchString ) ||
            st.searchName.includes( originalSearchString ) )
    ) ];

    if ( matchedEtfs.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'ETFs',
        items: [ ...matchedEtfs.map( stock => ( {
          full_name: stock.name,
          ticker: stock.ticker,
          isEtf: true
        } ) ) ]
      } );
    }
  }

  if ( hasSectorAccess.value && props.supportsSectors ) {
    const matchedSectors = [ ...sectorsStore.allSectors.filter( sector =>
        sector.symbol.toLocaleLowerCase().includes( originalSearchString ) ||
        sector.name.toLocaleLowerCase().includes( originalSearchString ) ) ];

    if ( matchedSectors.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'Sectors',
        items: [ ...matchedSectors.map( sector => ( {
          ticker: sector.symbol,
          full_name: sector.name,
          isSector: true
        } ) ) ]
      } );
    }
  }

  searchInputRef.value.overlayVisible = true;
  searchInputRef.value.searching = false;
}, 350, { maxWait: 5000 } );

function onSearch() {
    if ( !searchString.value ) {
        return;
    }

    debouncedSearch();
}

function handleSelection( selectedItem: AutoCompleteOptionSelectEvent ) {
    searchString.value = '';
    const selectedItemValue = selectedItem.value as SearchResult;
    reportSearchEvent( selectedItemValue.ticker );
    navigateToSelectedItem( selectedItemValue );
}

function navigateToSelectedItem( selectedItem: SearchResult ) {
    if ( selectedItem.isStock ) {
        props.stockClickHandler?.( selectedItem.ticker );
    } else if ( selectedItem.isEtf ) {
        props.etfClickHandler?.( selectedItem.ticker );
    } else if ( selectedItem.isSector ) {
        props.sectorClickHandler?.( selectedItem.ticker );
    }
}
</script>
